import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { isRebateParentCategory, isRebateSubCategory } from '../../../../../utils/vendorCategoryUtils';

// 컴포넌트 임포트
import VendorInfoTab from './tabs/vendorInfoTab';
import VendorProductsTab from './tabs/vendorProductsTab';
import VendorOrdersTab from './tabs/vendorOrdersTab';
// ✅ [추가] 새로 만든 모달 컴포넌트 import
import VendorEditModal, { type VendorEditData } from './modals/vendorEditModal'; 
import type { VendorBasicInfo, VendorProduct } from './types';

type TabType = 'BASIC' | 'PRODUCTS' | 'ORDERS' | 'SETTLEMENTS' | 'DOCUMENTS';

const normalizeBoolean = (value: unknown): boolean => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  if (typeof value === 'string') {
    const lowered = value.trim().toLowerCase();
    return lowered === 'true' || lowered === '1' || lowered === 'y' || lowered === 'yes';
  }
  return false;
};

const getIsAdminRole = () => {
  try {
    const raw = localStorage.getItem('userInfo');
    if (!raw) return false;
    const user = JSON.parse(raw);
    return normalizeBoolean(user?.isAdminRole) || user?.roleType === 'ADMIN';
  } catch {
    return false;
  }
};

const getMyVendorIdx = () => {
  try {
    const raw = localStorage.getItem('userInfo');
    if (!raw) return null;
    const user = JSON.parse(raw);
    const parsed = Number(user?.vendorIdx);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  } catch {
    return null;
  }
};

const isPartnershipVendor = (vendor: VendorBasicInfo | null) => {
  if (!vendor) return false;

  const contextType = String(vendor.contextType ?? '').trim().toUpperCase();
  const relationshipType = String(vendor.relationshipType ?? '').trim().toUpperCase();

  if (contextType === 'OWNER') return false;
  if (contextType === 'PARTNER') return true;
  if (relationshipType.includes('PARTNER')) return true;
  if (
    normalizeBoolean(vendor.isPartnerVendor) ||
    normalizeBoolean(vendor.isPartnershipVendor) ||
    normalizeBoolean(vendor.isGuestContext)
  ) {
    return true;
  }
  if (normalizeBoolean(vendor.isOwnerVendor)) return false;

  const myVendorIdx = getMyVendorIdx();
  const vendorIdx = Number(vendor.vendorIdx);
  if (myVendorIdx !== null && Number.isFinite(vendorIdx) && vendorIdx === myVendorIdx) {
    return false;
  }

  const ownerVendorIdx = Number(vendor.ownerVendorIdx);
  if (myVendorIdx !== null && Number.isFinite(ownerVendorIdx) && ownerVendorIdx > 0) {
    if (ownerVendorIdx !== myVendorIdx) return true;
  }

  const hostVendorIdx = Number(vendor.hostVendorIdx);
  if (myVendorIdx !== null && Number.isFinite(hostVendorIdx) && hostVendorIdx > 0) {
    if (hostVendorIdx !== myVendorIdx) return true;
  }

  return false;
};

export default function VendorDetail() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const vendorIdx = searchParams.get('id');

  // --- [State] ---
  const [activeTab, setActiveTab] = useState<string>('info');
  const [tabData, setTabData] = useState<any>(null);
  const [vendorBasicInfo, setVendorBasicInfo] = useState<VendorBasicInfo | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isAdminRole] = useState(getIsAdminRole);

  // 모달 상태
  const [showEditModal, setShowEditModal] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState<VendorProduct | null>(null);

  // 카테고리 (상품 등록 모달용)
  const [productCategoryTree, setProductCategoryTree] = useState<{ vendorCategoryIdx: number; name?: string; label?: string; children?: { vendorCategoryIdx: number; name?: string; label?: string }[] }[]>([]);
  const [selectedProductParentCat, setSelectedProductParentCat] = useState('');
  const [selectedProductSubCat, setSelectedProductSubCat] = useState('');

  useEffect(() => {
    if (!showProductModal) return;
    const fetchCategories = async () => {
      try {
        const token = localStorage.getItem('accessToken');
        const res = await fetch('https://api.wedmin.biz/vendor/category', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const result = await res.json();
        const tree = Array.isArray(result) ? result : result?.status ? result.data : [];
        setProductCategoryTree(tree);
      } catch (e) { console.error(e); }
    };
    fetchCategories();
    // 수정 시 기존 카테고리 세팅
    if (editingProduct?.category) {
      setSelectedProductSubCat(editingProduct.category);
    } else {
      setSelectedProductParentCat('');
      setSelectedProductSubCat('');
    }
  }, [showProductModal, editingProduct]);
  const partnershipVendor = useMemo(() => isPartnershipVendor(vendorBasicInfo), [vendorBasicInfo]);

  // --- [API] 데이터 호출 ---
  const fetchTabData = useCallback(async () => {
    if (!vendorIdx) return;
    setIsLoading(true);

    let apiTabName: TabType = 'BASIC';
    if (activeTab === 'info') apiTabName = 'BASIC';
    else if (activeTab === 'products') apiTabName = 'PRODUCTS';
    else if (activeTab === 'orders') apiTabName = 'ORDERS';

    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(
        `https://api.wedmin.biz/vendor/${vendorIdx}/detail?tab=${apiTabName}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const result = await response.json();
        if (result.status) {
          setTabData(result.data);
          if (apiTabName === 'BASIC') {
            setVendorBasicInfo(result.data);
          }
        }
      }
    } catch (error) {
      console.error('API Error:', error);
    } finally {
      setIsLoading(false);
    }
  }, [vendorIdx, activeTab]);

  useEffect(() => {
    fetchTabData();
  }, [fetchTabData]);

  // --- [Handlers] ---
  
  // ✅ [NEW] 업체 정보 수정 API 호출 핸들러
  const handleUpdateVendor = async (data: VendorEditData) => {
    if (!vendorIdx) return;

    try {
      const token = localStorage.getItem('accessToken');
      
      let bodyData: any;
      if (partnershipVendor) {
        bodyData = { status: data.status };
      } else {
        bodyData = {
          ...data,
          ...(vendorBasicInfo?.ownerVendorIdx ? { ownerVendorIdx: vendorBasicInfo.ownerVendorIdx } : {})
        };
        // 빈 문자열 필드 제거 (백엔드 validation 방지)
        if (!bodyData.notiPhone?.trim()) delete bodyData.notiPhone;
        if (!bodyData.email?.trim()) delete bodyData.email;
        if (!bodyData.businessNumber?.trim()) delete bodyData.businessNumber;
        if (!bodyData.address?.trim()) delete bodyData.address;
      }

      const response = await fetch(`https://api.wedmin.biz/vendor/${vendorIdx}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(bodyData)
      });

      if (response.ok) {
        alert(partnershipVendor ? '업체 상태가 수정되었습니다.' : '업체 정보가 수정되었습니다.');
        setShowEditModal(false);
        // 정보 갱신을 위해 기본정보 탭으로 강제 이동하거나 데이터 다시 로드
        if (activeTab !== 'info') setActiveTab('info');
        fetchTabData(); 
      } else {
        const errorData = await response.json().catch(() => null);
        if (response.status === 403) {
          alert(errorData?.message || '연결된 파트너 업체는 상태(ACTIVE/INACTIVE)만 변경할 수 있습니다.');
          return;
        }
        alert(`수정 실패: ${errorData?.message || '오류가 발생했습니다.'}`);
      }
    } catch (error) {
      console.error('Update Vendor Error:', error);
      alert('오류가 발생했습니다.');
    }
  };

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    setTabData(null);
  };

  // 1. 상품 등록 / 수정 API 호출
  const handleSaveProduct = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!vendorIdx) return;
    if (!isAdminRole) {
      alert('관리자만 상품을 등록/수정/삭제할 수 있습니다.');
      return;
    }

    const formData = new FormData(e.currentTarget);
    const isRebateProduct = isRebateParentCategory(selectedProductParentCat);

    // 혼수품목: contractItems를 name으로 사용, 가격/재고 기본값
    const productName = isRebateProduct
      ? (formData.get('contractItems') as string || '혼수품목')
      : formData.get('name');

    const productData: Record<string, unknown> = {
      partnerVendorIdx: Number(vendorIdx),
      name: productName,
      category: formData.get('category'),
      costPrice: Number(formData.get('costPrice') || 0),
      salesPrice: Number(formData.get('salesPrice') || 0),
      stockQuantity: Number(formData.get('stockQuantity') || 999),
      status: formData.get('status') || 'SALE',
      description: formData.get('description') || '',
      thumbnailUrl: '',
      ...(isRebateProduct ? {
        contractDate: formData.get('contractDate') || null,
        contractItems: formData.get('contractItems') || null,
        visitConsultation: formData.get('visitConsultation') || null,
        deposit: Number(formData.get('deposit') || 0) || null,
        rebateRate: Number(formData.get('rebateRate') || 0) || null,
        rebateAmount: Number(formData.get('rebateAmount') || 0) || null,
      } : {}),
    };

    try {
      const token = localStorage.getItem('accessToken');
      let url = 'https://api.wedmin.biz/product';
      let method = 'POST';

      if (editingProduct) {
        url = `https://api.wedmin.biz/product/${editingProduct.productIdx}`;
        method = 'PATCH';
      }

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(productData)
      });

      if (response.ok) {
        alert(editingProduct ? '상품이 수정되었습니다.' : '상품이 등록되었습니다.');
        setShowProductModal(false);
        fetchTabData();
      } else {
        const errorData = await response.json();
        alert(`저장 실패: ${errorData.message || '오류가 발생했습니다.'}`);
      }
    } catch (error) {
      console.error('Save Product Error:', error);
      alert('서버 통신 중 오류가 발생했습니다.');
    }
  };

  const handleDeleteProduct = async (productIdx: number) => {
    if (!isAdminRole) {
      alert('관리자만 상품을 등록/수정/삭제할 수 있습니다.');
      return;
    }
    if (!confirm('정말 이 상품을 삭제하시겠습니까?')) return;

    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`https://api.wedmin.biz/product/${productIdx}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        alert('상품이 삭제되었습니다.');
        fetchTabData();
      } else {
        const errorData = await response.json().catch(() => null);
        alert(`삭제 실패: ${errorData?.message || '오류가 발생했습니다.'}`);
      }
    } catch (error) {
      console.error('Delete Product Error:', error);
    }
  };

  const handleAddProduct = () => {
    setEditingProduct(null);
    setShowProductModal(true);
  };

  const handleEditProduct = (product: VendorProduct) => {
    setEditingProduct(product);
    setShowProductModal(true);
  };

  // --- [UI Helpers] ---
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE': return 'bg-green-100 text-green-700';
      case 'INACTIVE': return 'bg-red-100 text-red-700';
      case 'PENDING': return 'bg-yellow-100 text-yellow-700';
      case 'SUSPENDED': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 p-8">
        {/* 헤더 */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/dashboard/vendors')}
              className="text-gray-600 hover:text-gray-800 w-10 h-10 flex items-center justify-center hover:bg-gray-100 rounded transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line text-xl"></i>
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">업체 상세정보</h1>
              <p className="text-sm text-gray-600 mt-1">업체 정보 및 거래 내역을 확인하세요</p>
            </div>
          </div>
          <div className="flex gap-3">
            {/* 정보 수정 버튼 */}
            <button 
              onClick={() => setShowEditModal(true)}
              className="bg-blue-600 text-white px-6 py-2.5 rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap flex items-center gap-2 cursor-pointer"
            >
              <i className="ri-edit-line"></i> {partnershipVendor ? '상태 변경' : '정보 수정'}
            </button>

          </div>
        </div>

        {/* 업체 기본 정보 카드 */}
        {vendorBasicInfo && (
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <div className="flex items-start justify-between mb-6">
              <div className="flex items-center gap-6">
                <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center">
                  <i className="ri-building-line text-3xl text-blue-600"></i>
                </div>
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <h2 className="text-2xl font-bold text-gray-800">{vendorBasicInfo.name}</h2>
                    <span className={`inline-flex px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(vendorBasicInfo.status)}`}>
                      {vendorBasicInfo.status}
                    </span>
                    <span className="inline-flex px-3 py-1 text-sm font-medium rounded-full bg-blue-100 text-blue-700">
                      {vendorBasicInfo.type}
                    </span>
                  </div>
                  <div className="flex items-center gap-6 text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                      <i className="ri-user-line w-4 h-4 flex items-center justify-center"></i>
                      <span>담당: {vendorBasicInfo.managerName}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <i className="ri-phone-line w-4 h-4 flex items-center justify-center"></i>
                      <span>{vendorBasicInfo.contact}</span>
                    </div>
                    {vendorBasicInfo.notiPhone && (
                      <div className="flex items-center gap-2">
                        <i className="ri-chat-1-line w-4 h-4 flex items-center justify-center"></i>
                        <span>알림톡: {vendorBasicInfo.notiPhone}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <i className="ri-mail-line w-4 h-4 flex items-center justify-center"></i>
                      <span>{vendorBasicInfo.email}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="text-right">
                {/* <div className="flex items-center gap-2 mb-2">
                  <div className="flex text-yellow-400">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className={`ri-star-${i < Math.floor(vendorBasicInfo.rating || 5) ? 'fill' : 'line'} text-sm`}></i>
                    ))}
                  </div>
                  <span className="text-sm font-medium text-gray-700">{vendorBasicInfo.rating || 5.0}</span>
                </div>
                <p className="text-sm text-gray-600">평점</p> */}
              </div>
            </div>

            {/* 통계 정보 */}
            <div className="grid grid-cols-3 gap-6">
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-xs text-gray-500 mb-1">총 발주건수</div>
                <div className="text-xl font-bold text-gray-800">{vendorBasicInfo.totalOrderCount}건</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-xs text-gray-500 mb-1">총 거래금액</div>
                <div className="text-xl font-bold text-blue-600">{vendorBasicInfo.totalTransactionAmount.toLocaleString()}원</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-xs text-gray-500 mb-1">최근 발주일</div>
                <div className="text-xl font-bold text-gray-800">{vendorBasicInfo.lastOrderDate}</div>
              </div>
            </div>

            {/* 초기 로그인 정보 (비밀번호 미변경 시에만 표시) */}
            {vendorBasicInfo.credential && (
              <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <i className="ri-key-line text-amber-600"></i>
                  <span className="text-sm font-semibold text-amber-800">초기 로그인 정보 (비밀번호 미변경)</span>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">로그인 ID: </span>
                    <span className="font-medium text-gray-900">{vendorBasicInfo.credential.loginId}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">임시 비밀번호: </span>
                    <span className="font-mono font-medium text-gray-900">{vendorBasicInfo.credential.tempPassword}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* 탭 메뉴 (기존 유지) */}
        <div className="bg-white rounded-lg shadow-sm mb-6">
          <div className="flex border-b">
            {[
              { id: 'info', label: '기본정보', icon: 'ri-information-line' },
              { id: 'orders', label: '발주내역', icon: 'ri-file-list-line' },
              // 리베이트 기반 업체(혼수, 웨딩홀, 허니문)는 상품관리 탭 숨김
              ...(!isRebateSubCategory(vendorBasicInfo?.type || '') ? [{ id: 'products', label: '상품관리', icon: 'ri-shopping-bag-line' }] : []),
              // { id: 'settlement', label: '정산내역', icon: 'ri-money-dollar-circle-line' },
              // { id: 'documents', label: '서류관리', icon: 'ri-file-text-line' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`flex-1 px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600 bg-blue-50'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                <i className={`${tab.icon} mr-2`}></i>
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* 탭 컨텐츠 (기존 유지) */}
        {isLoading ? (
           <div className="flex justify-center p-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>
        ) : (
          <>
            {activeTab === 'info' && <VendorInfoTab data={tabData} />}
            {activeTab === 'orders' && <VendorOrdersTab data={Array.isArray(tabData) ? tabData : []} />}
            {activeTab === 'products' && (
              <VendorProductsTab 
                data={Array.isArray(tabData) ? tabData : []} 
                canManageProducts={isAdminRole}
                onAddProduct={handleAddProduct}
                onEditProduct={handleEditProduct}
                onDeleteProduct={handleDeleteProduct}
              />
            )}
            {(activeTab === 'settlement' || activeTab === 'documents') && (
              <div className="bg-white rounded-lg shadow-sm p-6 text-center py-12">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className={`text-2xl text-gray-500 ${activeTab === 'settlement' ? 'ri-money-dollar-circle-line' : 'ri-file-text-line'}`}></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {activeTab === 'settlement' ? '정산 내역' : '서류 관리'}
                </h3>
                <p className="text-gray-600">준비 중입니다.</p>
              </div>
            )}
          </>
        )}

        {/* 상품 등록 모달 (기존 유지) */}
        {showProductModal && isAdminRole && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold">{editingProduct ? '상품 수정' : '상품 등록'}</h2>
                <button onClick={() => setShowProductModal(false)}><i className="ri-close-line text-2xl cursor-pointer"></i></button>
              </div>
              
              <form onSubmit={handleSaveProduct} className="space-y-5">
                {/* 카테고리 선택 (항상 표시) */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">대분류 *</label>
                    <select
                      value={selectedProductParentCat}
                      onChange={(e) => {
                        setSelectedProductParentCat(e.target.value);
                        setSelectedProductSubCat('');
                      }}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 pr-8"
                    >
                      <option value="">대분류 선택</option>
                      {productCategoryTree.map((cat) => (
                        <option key={cat.vendorCategoryIdx} value={cat.label || cat.name}>{cat.label || cat.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">소분류 (카테고리) *</label>
                    <select
                      name="category"
                      value={selectedProductSubCat}
                      onChange={(e) => setSelectedProductSubCat(e.target.value)}
                      required
                      disabled={!selectedProductParentCat}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 pr-8"
                    >
                      <option value="">소분류 선택</option>
                      {(productCategoryTree.find((c) => (c.label || c.name) === selectedProductParentCat)?.children || []).map((sub) => (
                        <option key={sub.vendorCategoryIdx} value={sub.label || sub.name}>{sub.label || sub.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* ===== 혼수업체: 혼수 전용 폼 ===== */}
                {isRebateParentCategory(selectedProductParentCat) ? (
                  <div className="space-y-4">
                    <h4 className="text-sm font-semibold text-purple-700 flex items-center gap-1 border-b border-gray-200 pb-2">
                      <i className="ri-gift-line"></i> 리베이트 계약 정보
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">계약일자</label>
                        <input type="date" name="contractDate" className="w-full border border-gray-300 rounded-md px-3 py-2" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">방문상담</label>
                        <input type="text" name="visitConsultation" className="w-full border border-gray-300 rounded-md px-3 py-2" placeholder="예: 2026-04-05 방문" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">계약품목 *</label>
                      <input type="text" name="contractItems" required className="w-full border border-gray-300 rounded-md px-3 py-2" placeholder="예: 양복 2벌, 셔츠 3장" />
                      {/* 계약품목을 상품명으로도 사용 */}
                      <input type="hidden" name="name" value="" />
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">계약금</label>
                        <input type="number" name="deposit" min="0" className="w-full border border-gray-300 rounded-md px-3 py-2" placeholder="0" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">리베이트 (%)</label>
                        <input type="number" name="rebateRate" step="0.1" min="0" max="100" className="w-full border border-gray-300 rounded-md px-3 py-2" placeholder="%" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">리베이트 금액</label>
                        <input type="number" name="rebateAmount" min="0" className="w-full border border-gray-300 rounded-md px-3 py-2" placeholder="0" />
                      </div>
                    </div>
                    {/* 혼수품목은 재고/상태 기본값 */}
                    <input type="hidden" name="stockQuantity" value="999" />
                    <input type="hidden" name="status" value="SALE" />
                  </div>
                ) : (
                  /* ===== 일반 상품 폼 ===== */
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">상품명 *</label>
                      <input type="text" name="name" defaultValue={editingProduct?.name} required className="w-full border border-gray-300 rounded-md px-3 py-2" placeholder="상품명을 입력하세요" />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">판매 상태 *</label>
                        <select name="status" defaultValue={editingProduct?.status || 'SALE'} required className="w-full border border-gray-300 rounded-md px-3 py-2 pr-8">
                          <option value="SALE">판매중 (SALE)</option>
                          <option value="SOLDOUT">품절 (SOLDOUT)</option>
                          <option value="STOP">판매중지 (STOP)</option>
                        </select>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">공급가 (원가) *</label>
                        <input type="number" name="costPrice" defaultValue={editingProduct?.costPrice} required min="0" className="w-full border border-gray-300 rounded-md px-3 py-2" placeholder="예: 800000" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">판매가 (고객가) *</label>
                        <input type="number" name="salesPrice" defaultValue={editingProduct?.salesPrice} required min="0" className="w-full border border-gray-300 rounded-md px-3 py-2" placeholder="예: 1200000" />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">재고 수량 *</label>
                      <input type="number" name="stockQuantity" defaultValue={editingProduct?.stockQuantity || 999} required min="0" className="w-full border border-gray-300 rounded-md px-3 py-2" />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">상품 설명</label>
                      <textarea name="description" defaultValue={editingProduct?.description} rows={4} maxLength={500} className="w-full border border-gray-300 rounded-md px-3 py-2 resize-none" placeholder="상품 설명을 입력하세요" />
                    </div>
                  </>
                )}

                <div className="flex justify-end gap-3 mt-8">
                  <button type="button" onClick={() => setShowProductModal(false)} className="px-6 py-2 border rounded-md hover:bg-gray-50">취소</button>
                  <button type="submit" className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">{editingProduct ? '수정 완료' : '등록하기'}</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ✅ [수정] 실제 동작하는 정보 수정 모달 연결 */}
        <VendorEditModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          initialData={vendorBasicInfo}
          statusOnly={partnershipVendor}
          onSave={handleUpdateVendor}
        />
      </div>
    </div>
  );
}
